"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  countElements,
  formatXmlDocument,
  maxElementDepth,
} from "@/lib/tool-kit/developer/xml-format";

type Mode = "format" | "minify";
type IndentChoice = "2" | "4" | "tab";

const MODES: Mode[] = ["format", "minify"];

const INDENT_CHOICES: IndentChoice[] = ["2", "4", "tab"];

function resolveIndent(choice: IndentChoice): string {
  if (choice === "tab") {
    return "\t";
  }
  return " ".repeat(Number(choice));
}

type ProcessResult =
  | { ok: true; output: string; elements: number; depth: number }
  | { ok: false; error: string };

function processXml(
  input: string,
  mode: Mode,
  indentChoice: IndentChoice,
  strings: Strings
): ProcessResult {
  const doc = new DOMParser().parseFromString(input, "application/xml");
  const parserError = doc.querySelector("parsererror");
  if (parserError !== null) {
    const detail = (
      parserError.textContent ?? strings.unknownParseError
    ).trim();
    return {
      ok: false,
      error: strings.parseErrorPrefix.replace("{detail}", detail),
    };
  }
  const output = formatXmlDocument(
    doc,
    input,
    resolveIndent(indentChoice),
    mode === "format"
  );
  return {
    ok: true,
    output,
    elements: countElements(doc),
    depth: maxElementDepth(doc),
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const XML_FORMATTER_STRINGS = {
  mode: "Mode",
  indent: "Indent:",
  xmlInput: "XML input",
  inputPlaceholder:
    '<library><book id="1"><title>APA Manual</title></book></library>',
  format: "Format",
  minify: "Minify",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  formattedXml: "Formatted XML",
  minifiedXml: "Minified XML",
  elements: "Elements",
  maxDepth: "Max depth",
  outputSize: "Output size",
  charsCount: "one:{count} char|other:{count} chars",
  modeLabels: {
    format: "Format (pretty-print)",
    minify: "Minify",
  },
  indentLabels: {
    "2": "2 spaces",
    "4": "4 spaces",
    tab: "Tab",
  },
  parseErrorPrefix:
    "This is not well-formed XML. The parser reported:\n{detail}",
  unknownParseError: "Unknown parse error",
} as const;

type Strings = typeof XML_FORMATTER_STRINGS;

export default function XmlFormatterWidget() {
  const strings = useToolUi(XML_FORMATTER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<Mode>("format");
  const [indentChoice, setIndentChoice] = useState<IndentChoice>("2");
  const [output, setOutput] = useState("");
  const [error, setError] = useState("");
  const [stats, setStats] = useState<{
    elements: number;
    depth: number;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  function handleProcess() {
    if (input.trim().length === 0) {
      return;
    }
    const result = processXml(input, mode, indentChoice, strings);
    if (result.ok) {
      setOutput(result.output);
      setStats({ elements: result.elements, depth: result.depth });
      setError("");
      setCopied(false);
    } else {
      setError(result.error);
      setOutput("");
      setStats(null);
    }
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setError("");
    setStats(null);
    setCopied(false);
  }

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.mode}</p>
        <div className="flex flex-wrap gap-3">
          {MODES.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m}
            >
              <input
                checked={mode === m}
                className="accent-primary"
                name="xml-mode"
                onChange={() => setMode(m)}
                type="radio"
                value={m}
              />
              {strings.modeLabels[m]}
            </label>
          ))}
        </div>
        {mode === "format" && (
          <label className="flex items-center gap-2 text-foreground text-sm">
            {strings.indent}
            <select
              className="rounded-md border border-border bg-card px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              onChange={(e) => setIndentChoice(e.target.value as IndentChoice)}
              value={indentChoice}
            >
              {INDENT_CHOICES.map((c) => (
                <option key={c} value={c}>
                  {strings.indentLabels[c]}
                </option>
              ))}
            </select>
          </label>
        )}
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="xml-input"
        >
          {strings.xmlInput}
        </label>
        <textarea
          className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="xml-input"
          onChange={(e) => {
            setInput(e.target.value);
            setCopied(false);
          }}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Error */}
      {error.length > 0 && (
        <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border border-destructive/40 bg-destructive/5 p-4 font-mono text-destructive text-sm">
          {error}
        </pre>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleProcess}
          type="button"
        >
          {mode === "format" ? strings.format : strings.minify}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="xml-output"
          >
            {mode === "format" ? strings.formattedXml : strings.minifiedXml}
          </label>
          <textarea
            className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="xml-output"
            readOnly
            value={output}
          />
        </div>
      )}

      {/* Stats */}
      {stats !== null && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.elements} value={stats.elements} />
          <StatCard label={strings.maxDepth} value={stats.depth} />
          <StatCard
            label={strings.outputSize}
            value={tPlural(strings.charsCount, locale, output.length, {
              count: fmt(output.length),
            })}
          />
        </div>
      )}
    </>
  );
}
